import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { NormalizedResponseData } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/entities';
import { AxiosError } from 'axios';
import { Box, Link, Typography, useTheme, makeStyles } from '@dxb-web-ui-framework/design-system';
import { isEmpty, isFunction } from '@dxb-web-ui-framework/common/utils';
import { useTranslation } from 'react-i18next';
import { invokeService, useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { ECOMMBXLoader } from '../../../../../Generic/ECOMMBXLoader/ECOMMBXLoader';
import { ErrorAlert } from '../../../../../Generic/ErrorAlert/ErrorAlert';
import { ReactComponent as PushNotificationIcon } from '../../../../../../assets/design-system-icons/push-notification-icon.svg';
import { useWizardErrorState, useWizardGoToNextStep } from '../../../../../../contexts/ClientSideWizard/hooks';
import { IAuthorizationsService } from '../../../../../../../../iam/src/domain/Authorizations/entities/IAuthorizationsService';
import { useUserInfo } from '../../../../../../../../userProfile/src/contexts/UserInfo/hooks';
import { AuthenticationMethodEnum } from '../../InAPP2FAStep';

export interface SCAProps {
  authorizationsService: IAuthorizationsService;
  authorizationId: number;
  changeAuthorizationId: (authorizationId: number) => void;
  title?: string;
  authMethod?: AuthenticationMethodEnum;
  resetFlow?: () => void;
  isLeftPositioned?: boolean;
  onToggleDrawer?: Function;
  externalOnGoToNextWizardStep?: (data: any) => void;
  externalErrors?: Nullable<NormalizedResponseData<any> | AxiosError<unknown, any>>;
  externalErrorSetter?: React.Dispatch<
    React.SetStateAction<Nullable<NormalizedResponseData<any> | AxiosError<unknown, any>>>
  >;
}

const useStyles = makeStyles(theme => ({
  linkBtn: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  resendLinkBtn: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  userText: {
    marginLeft: theme.spacing(0.5),
    textDecoration: 'underline',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
  },
  bottomCentered: {
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      left: '50%',
      bottom: theme.spacing(1),
      transform: 'translate(-50%, -50%)',
      margin: '0 auto',
    },
  },
  title: {
    whiteSpace: 'pre-line',
  },
}));

export enum SCAPollingResponse {
  CONFIRMED = 'CONFIRMED',
  REJECTED = 'REJECTED',
  PENDING = 'PENDING',
  EXPIRED = 'EXPIRED',
  CANCELED = 'CANCELED',
}

//TODO: wait for middleware's team response to resolve the issue with the fallback to OTP endpoint
const SCA: FC<SCAProps> = ({
  authorizationsService,
  authorizationId,
  changeAuthorizationId,
  title,
  resetFlow,
  authMethod,
  isLeftPositioned,
  onToggleDrawer,
  externalOnGoToNextWizardStep,
  externalErrors,
  externalErrorSetter,
}) => {
  const { t } = useTranslation();
  const { setNetworkCallErrors, errors: wizardErrorStateErrors } = useWizardErrorState();
  const goToNextStep = useWizardGoToNextStep();
  const userInfo = useUserInfo();
  const expiredErrorMessage = t('security.settings.changePasswordWizard.SCA.expired.ErrorMessage');
  const rejectedErrorMessage = t('security.settings.changePasswordWizard.SCA.rejected.ErrorMessage');
  const [shouldChangeMethod, setShouldChangeMethod] = React.useState(false);

  const setNetworkCallErrorsCb = useCallback(
    error => {
      if (externalErrorSetter) {
        externalErrorSetter(error);
        return;
      }
      setNetworkCallErrors(error);
    },
    [setNetworkCallErrors, externalErrorSetter],
  );

  const goToNextStepCb = useCallback(data => goToNextStep(data), [goToNextStep]);
  const resetFlowCb = useCallback(() => {
    resetFlow && resetFlow();
  }, [resetFlow]);

  const theme = useTheme();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();

  const { data, error } = useInvokeSWRService(
    authorizationsService.WebAuthorization,
    { authorizationId },
    Boolean(authorizationId) && isEmpty(wizardErrorStateErrors),
    'authorizationsService.WebAuthorization',
    {
      refreshInterval: isEmpty(wizardErrorStateErrors) ? 5000 : 0,
    },
  );

  const {
    data: changeMethodData,
    isLoading: changeMethodIsLoading,
    error: changeMethodError,
  } = useInvokeSWRService(
    authorizationsService.FallbackToOTP,
    { authorizationId },
    Boolean(shouldChangeMethod) && authMethod === AuthenticationMethodEnum.SCA,
    'authorizationsService.FallbackToOTP',
  );

  useEffect(() => {
    if (changeMethodData && !changeMethodError && !changeMethodIsLoading) {
      changeAuthorizationId(changeMethodData.id);
      setShouldChangeMethod(false);
    }
    if (changeMethodError) {
      setNetworkCallErrorsCb(error);
      return;
    }
  }, [
    changeMethodData,
    changeMethodError,
    changeMethodIsLoading,
    changeAuthorizationId,
    setShouldChangeMethod,
    setNetworkCallErrorsCb,
    error,
  ]);

  const onChangeMethod = useCallback(async () => {
    setShouldChangeMethod(true);
  }, [setShouldChangeMethod]);

  const onSendPushNotificationAgain = useCallback(async () => {
    if (!isEmpty(wizardErrorStateErrors)) {
      setNetworkCallErrorsCb(null);
    }
    const { error } = await invokeService(() => authorizationsService.ResendCode({ authorizationId }));
    if (error) {
      setNetworkCallErrorsCb(error);
      return;
    }
  }, [authorizationId, authorizationsService, setNetworkCallErrorsCb, wizardErrorStateErrors]);

  useEffect(() => {
    if (error) {
      setNetworkCallErrorsCb(error);
      return;
    }
    if (data?.status === SCAPollingResponse.CONFIRMED) {
      if (externalOnGoToNextWizardStep && isFunction(externalOnGoToNextWizardStep)) {
        externalOnGoToNextWizardStep(data);
      } else {
        goToNextStepCb(data);
      }
    } else if (data?.status === SCAPollingResponse.EXPIRED && !changeMethodIsLoading && !Boolean(changeMethodData)) {
      setNetworkCallErrorsCb({
        status: 400,
        message: expiredErrorMessage,
      });
    } else if (data?.status === SCAPollingResponse.REJECTED) {
      setNetworkCallErrorsCb({
        status: 400,
        message: rejectedErrorMessage,
      });
      setTimeout(() => {
        onToggleDrawer && onToggleDrawer();
      }, 4000);
    }
  }, [
    externalErrorSetter,
    error,
    data,
    expiredErrorMessage,
    setNetworkCallErrorsCb,
    goToNextStepCb,
    rejectedErrorMessage,
    onToggleDrawer,
    externalOnGoToNextWizardStep,
    changeMethodIsLoading,
    changeMethodData,
  ]);

  const errors = useMemo(() => {
    if (!wizardErrorStateErrors && !externalErrors) {
      return null;
    }

    if (externalErrors) {
      return <ErrorAlert message={(externalErrors as any)?.message} />;
    }

    if (wizardErrorStateErrors && !wizardErrorStateErrors.headers) {
      return <ErrorAlert message={wizardErrorStateErrors.message} />;
    }

    return wizardErrorStateErrors?.headers.map(({ message }: { message: string }) => <ErrorAlert message={message} />);
  }, [wizardErrorStateErrors, externalErrors]);

  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined = undefined;
    if (!isEmpty(errors)) {
      if (!changeMethodIsLoading && !Boolean(changeMethodData)) {
        timerId = setTimeout(resetFlowCb, 3000);
      }
    }
    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [data, resetFlowCb, errors, setNetworkCallErrorsCb, changeMethodIsLoading, changeMethodData]);

  const notificationIconDimensionsCalculation = isMobile ? `${theme.spacing(24)}px` : `${theme.spacing(15)}px`;

  const classes = useStyles();

  // TODO: Merge the two different renders for mobile and desktop into one.
  if (isMobile) {
    return (
      <Box
        data-testid={'inApp2FAStep-SCA'}
        display='flex'
        flexDirection='column'
        alignItems={'center'}
        height='100%'
        px={1}
        width={'100%'}
        pt={4}
        pb={1}>
        {title && (
          <Box mb={errors ? 3 : 8}>
            <Typography className={classes.title} variant='h6'>
              {title}
            </Typography>
          </Box>
        )}
        {errors && <Box pb={1}>{errors}</Box>}
        <Box display='flex' alignItems='center' justifyContent='center' pb={4}>
          <PushNotificationIcon
            width={notificationIconDimensionsCalculation}
            height={notificationIconDimensionsCalculation}
          />
        </Box>
        <Typography variant='body2' color='textSecondary'>
          {t('content-composer.components.InApp2FA.SCA.Typography.text1')}
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          {t('content-composer.components.InApp2FA.SCA.Typography.text2')}{' '}
          {t('content-composer.components.InApp2FA.SCA.Typography.text3')}
        </Typography>
        <Box display='flex' flex={1}>
          <Typography variant='body2' className={classes.userText}>
            {userInfo?.firstname}
          </Typography>
          <Link size='small' onClick={onSendPushNotificationAgain} classes={{ root: classes.resendLinkBtn }}>
            ({t('content-composer.components.InApp2FA.SCA.ResendNotificationButton.text')})
          </Link>
        </Box>
        <Box className={classes.bottomCentered}>
          <Link size='small' onClick={onChangeMethod} classes={{ root: classes.linkBtn }}>
            {t('content-composer.components.InApp2FA.SCA.ChangeToOTPButton.Text')}
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid={'inApp2FAStep-SCA'}
      display='flex'
      flexDirection='column'
      height={'100%'}
      alignItems={isLeftPositioned ? 'flex-start' : 'center'}>
      {title && (
        <Box mb={errors ? 2 : 5}>
          <Typography className={classes.title} variant='h4'>
            {title}
          </Typography>
        </Box>
      )}
      {errors && <Box>{errors}</Box>}
      {changeMethodIsLoading || Boolean(changeMethodData) ? (
        <ECOMMBXLoader />
      ) : (
        <>
          <Box>
            <Typography variant='body2' color='textSecondary'>
              {t('content-composer.components.InApp2FA.SCA.Typography.text1')}{' '}
              {t('content-composer.components.InApp2FA.SCA.Typography.text2')}
            </Typography>
          </Box>
          <Box display='flex'>
            <Typography variant='body2' color='textSecondary'>
              {t('content-composer.components.InApp2FA.SCA.Typography.text3')}
            </Typography>
            <Typography variant='body2' className={classes.userText}>
              {userInfo?.firstname}
            </Typography>
            <Link size='small' onClick={onSendPushNotificationAgain} classes={{ root: classes.resendLinkBtn }}>
              ({t('content-composer.components.InApp2FA.SCA.ResendNotificationButton.text')})
            </Link>
          </Box>
          <Box display='flex' alignItems='center' justifyContent='center' pt={4} pb={0}>
            <PushNotificationIcon
              width={notificationIconDimensionsCalculation}
              height={notificationIconDimensionsCalculation}
            />
          </Box>
          <Box display='flex' justifyContent='center' mt={5} flex={0}>
            <Link size='small' onClick={onChangeMethod} classes={{ root: classes.linkBtn }}>
              {t('content-composer.components.InApp2FA.SCA.ChangeToOTPButton.Text')}
            </Link>
          </Box>
        </>
      )}
    </Box>
  );
};

export { SCA };

export default SCA;
